import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

interface Item {
  id: string;
  label: string;
  /** Optional second line shown below label in smaller grey text */
  secondaryLabel?: string;
  /** Optional date/tag shown right-aligned in the row */
  trailingLabel?: string;
  selected?: boolean;
}

interface DualListMAIQProps {
  availableItems: Item[];
  selectedItems: Item[];
  onUpdate?: (selectedItems: Item[]) => void;
  listHeight?: number;
  /** Optional: return a ReactNode to show as a hover detail card for a given item id */
  renderHoverContent?: (id: string) => React.ReactNode;
}

const tooltipCardSx = {
  bgcolor: 'background.paper',
  color: 'text.primary',
  p: 0,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
  borderRadius: 2,
  maxWidth: 320,
  overflow: 'hidden',
};

const tooltipArrowSx = {
  color: 'background.paper',
  '&::before': { border: '1px solid', borderColor: 'divider' },
};

const normalizeItem = (item: Item): Item => ({
  ...item,
  id: item?.id != null ? String(item.id) : '',
  label: item?.label != null && item.label !== '' ? String(item.label) : '—',
  secondaryLabel:
    item?.secondaryLabel != null && item.secondaryLabel !== ''
      ? String(item.secondaryLabel)
      : undefined,
  trailingLabel:
    item?.trailingLabel != null && item.trailingLabel !== ''
      ? String(item.trailingLabel)
      : undefined,
  selected: false,
});

const DualListMAIQ: React.FC<DualListMAIQProps> = ({
  availableItems: initialAvailableItems = [],
  selectedItems: initialSelectedItems = [],
  onUpdate,
  listHeight = 300,
  renderHoverContent,
}) => {
  const [availableItems, setAvailableItems] = useState<Item[]>(() =>
    (initialAvailableItems ?? []).map(normalizeItem)
  );
  const [selectedItems, setSelectedItems] = useState<Item[]>(() =>
    (initialSelectedItems ?? []).map(normalizeItem)
  );
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  // Parent already has the correct selection; avoid firing onUpdate on mount
  // (it rewrites role.scopes / connected_resources and can remount/corrupt state).
  const isFirstSelectedSync = React.useRef(true);

  const filteredAvailable = availableItems.filter(item => {
    const search = availableSearch.toLowerCase();
    const matchPrimary = String(item.label ?? '').toLowerCase().includes(search);
    const matchSecondary = item.secondaryLabel
      ? String(item.secondaryLabel).toLowerCase().includes(search)
      : false;
    return matchPrimary || matchSecondary;
  });
  const filteredSelected = selectedItems.filter(item => {
    const search = selectedSearch.toLowerCase();
    const matchPrimary = String(item.label ?? '').toLowerCase().includes(search);
    const matchSecondary = item.secondaryLabel
      ? String(item.secondaryLabel).toLowerCase().includes(search)
      : false;
    return matchPrimary || matchSecondary;
  });

  useEffect(() => {
    if (isFirstSelectedSync.current) {
      isFirstSelectedSync.current = false;
      return;
    }
    if (onUpdate) {
      onUpdate(selectedItems);
    }
  }, [selectedItems]);

  const toggleSelection = (id: string, listType: "available" | "selected") => {
    if (listType === "available") {
      setAvailableItems(availableItems.map(item =>
        item.id === id ? { ...item, selected: !item.selected } : item
      ));
    } else {
      setSelectedItems(selectedItems.map(item =>
        item.id === id ? { ...item, selected: !item.selected } : item
      ));
    }
  };

  const moveSelected = (direction: "left" | "right") => {
    if (direction === "right") {
      const itemsToMove = availableItems.filter(item => item.selected);
      setAvailableItems(availableItems.filter(item => !item.selected));
      setSelectedItems([...selectedItems, ...itemsToMove.map(item => ({ ...item, selected: false }))]);
    } else {
      const itemsToMove = selectedItems.filter(item => item.selected);
      setSelectedItems(selectedItems.filter(item => !item.selected));
      setAvailableItems([...availableItems, ...itemsToMove.map(item => ({ ...item, selected: false }))]);
    }
  };

  const moveAll = (direction: "left" | "right") => {
    if (direction === "right") {
      setSelectedItems([...selectedItems, ...availableItems.map(item => ({ ...item, selected: false }))]);
      setAvailableItems([]);
    } else {
      setAvailableItems([...availableItems, ...selectedItems.map(item => ({ ...item, selected: false }))]);
      setSelectedItems([]);
    }
  };

  return (
    <Box>
      <Box display="flex" gap={4} p={2}>
        {/* Available Items List — LEFT */}
        <Paper sx={{ width: "100%" }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">Available options</Typography>
              <Chip
                label={`${availableItems.filter(i => i.selected).length} of ${availableItems.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search available items..."
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <List sx={{ height: listHeight, overflow: 'auto' }}>
            {filteredAvailable.length === 0 ? (
              <ListItem
                sx={{
                  height: 30,
                  opacity: 0.5,
                  fontStyle: "italic",
                  justifyContent: "center",
                }}
              >
                No items available
              </ListItem>
            ) : (
              filteredAvailable.map(item => (
                <Tooltip
                  key={item.id}
                  title={renderHoverContent ? (renderHoverContent(item.id) ?? '') : ''}
                  placement="top"
                  arrow
                  enterDelay={400}
                  leaveDelay={100}
                  componentsProps={{ tooltip: { sx: tooltipCardSx }, arrow: { sx: tooltipArrowSx } }}
                >
                  <ListItem
                    sx={{
                      minHeight: item.secondaryLabel ? 52 : 30,
                      height: 'auto',
                      py: item.secondaryLabel ? 0 : 0.5,
                      mb: 0.5,
                      borderRadius: 1,
                      backgroundColor: item.selected ? "rgba(255, 165, 0, 0.2)" : "background.paper",
                      cursor: "pointer",
                      alignItems: 'flex-start',
                      '&:hover': {
                        backgroundColor: item.selected ? "rgba(255, 165, 0, 0.3)" : "action.hover",
                      },
                    }}
                    onClick={() => toggleSelection(item.id, "available")}
                  >
                    <ListItemText
                      primary={item.label}
                      secondary={item.secondaryLabel}
                      primaryTypographyProps={{ fontWeight: item.selected ? 'bold' : 600, fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ sx: { fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 } }}
                      sx={{ flex: 1, minWidth: 0, mr: item.trailingLabel ? 1 : 0 }}
                    />
                    {item.trailingLabel && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: '0.7rem', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'flex-end', pb: 0.5 }}
                      >
                        {item.trailingLabel}
                      </Typography>
                    )}
                  </ListItem>
                </Tooltip>
              ))
            )}
          </List>
        </Paper>

        {/* Transfer Controls — → points toward current (right), ← points toward available (left) */}
        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
          <Tooltip title="Move all to current">
            <IconButton
              onClick={() => moveAll("right")}
              size="large"
              color="primary"
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <KeyboardDoubleArrowRight fontSize="large" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move highlighted to current">
            <span>
              <IconButton
                onClick={() => moveSelected("right")}
                disabled={availableItems.filter(i => i.selected).length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardArrowRight fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move highlighted to available">
            <span>
              <IconButton
                onClick={() => moveSelected("left")}
                disabled={selectedItems.filter(i => i.selected).length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardArrowLeft fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move all to available">
            <IconButton
              onClick={() => moveAll("left")}
              size="large"
              color="primary"
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <KeyboardDoubleArrowLeft fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Current / Selected Items List — RIGHT */}
        <Paper sx={{ width: "100%" }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">Current options</Typography>
              <Chip
                label={`${selectedItems.filter(i => i.selected).length} of ${selectedItems.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search current items..."
              value={selectedSearch}
              onChange={(e) => setSelectedSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <List sx={{ height: listHeight, overflow: 'auto' }}>
            {filteredSelected.length === 0 ? (
              <ListItem
                sx={{
                  height: 30,
                  opacity: 0.5,
                  fontStyle: "italic",
                  justifyContent: "center",
                }}
              >
                No items selected
              </ListItem>
            ) : (
              filteredSelected.map(item => (
                <Tooltip
                  key={item.id}
                  title={renderHoverContent ? (renderHoverContent(item.id) ?? '') : ''}
                  placement="top"
                  arrow
                  enterDelay={400}
                  leaveDelay={100}
                  componentsProps={{ tooltip: { sx: tooltipCardSx }, arrow: { sx: tooltipArrowSx } }}
                >
                  <ListItem
                    sx={{
                      minHeight: item.secondaryLabel ? 52 : 30,
                      height: 'auto',
                      py: item.secondaryLabel ? 0 : 0.5,
                      mb: 0.5,
                      borderRadius: 1,
                      backgroundColor: item.selected ? "rgba(255, 165, 0, 0.2)" : "background.paper",
                      cursor: "pointer",
                      alignItems: 'flex-start',
                      '&:hover': {
                        backgroundColor: item.selected ? "rgba(255, 165, 0, 0.3)" : "action.hover",
                      },
                    }}
                    onClick={() => toggleSelection(item.id, "selected")}
                  >
                    <ListItemText
                      primary={item.label}
                      secondary={item.secondaryLabel}
                      primaryTypographyProps={{ fontWeight: item.selected ? 'bold' : 600, fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ sx: { fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 } }}
                      sx={{ flex: 1, minWidth: 0, mr: item.trailingLabel ? 1 : 0 }}
                    />
                    {item.trailingLabel && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: '0.7rem', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'flex-end', pb: 0.5 }}
                      >
                        {item.trailingLabel}
                      </Typography>
                    )}
                  </ListItem>
                </Tooltip>
              ))
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

DualListMAIQ.defaultProps = {
  onUpdate: undefined,
};

export default DualListMAIQ;
